export enum TicketCategory {
  SWA_REPORTING_BUG = 'SWA_REPORTING_BUG',
  SWA_FEATURE_REQUEST = 'SWA_FEATURE_REQUEST',
  SST_ISSUE = 'SST_ISSUE',
  THR_ISSUE = 'THR_ISSUE',
  D2D_ISSUE = 'D2D_ISSUE',
  GENERAL_TECH_ISSUE = 'GENERAL_TECH_ISSUE',
  GENERAL_SUPPORT_ISSUE = 'GENERAL_SUPPORT_ISSUE',
  TEAM_ASSIGNMENT = 'TEAM_ASSIGNMENT',
  MISSING_PROJECT_DATA = 'MISSING_PROJECT_DATA'
}

export enum TicketStatus {
  OPEN = 'open',
  IN_PROGRESS = 'in_progress',
  RESOLVED = 'resolved',
  CLOSED = 'closed'
}

export enum TicketPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent'
}

/**
 * A person embedded on a ticket or a comment.
 *
 * ⚠️ SUB-1.47 · F-106 · THIS REPLACED A `{ id, name, email }` SHAPE, AND THE
 * MISSING `name` IS THE POINT. These fields used to come from the
 * `user_profiles` matview, which synthesised `name` as
 * `first_name || ' ' || last_name`, falling back to the email — a SECOND
 * definition of how a person is named, living in a database object, disagreeing
 * with `getUserDisplayName` on inactive and soft-deleted users.
 *
 * `users` has no `name` column, so callers compose it through
 * `getUserDisplayName`, which stays the one definition. It is also why 14
 * soft-deleted ticket creators now correctly render `Name [Inactive]` where the
 * matview showed them as ordinary colleagues.
 *
 * The field list mirrors `UserRecord` so the two interoperate without a cast.
 */
export interface TicketPerson {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  is_active?: boolean;
  deleted_at?: string | null;
  avatar_url?: string | null;
}

export interface Ticket {
  id: string;
  title: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  internal_priority?: TicketPriority;
  category: TicketCategory;
  loom_link?: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  assigned_to: string | null;
  project_id?: string;
  due_date?: string;
  created_by_user?: TicketPerson;
  assigned_user?: TicketPerson;
  unread_comments_count?: number;
}

export interface TicketComment {
  id: string;
  ticket_id: string;
  content: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  is_internal: boolean;
  mentioned_user_ids?: string[];
  created_by_user?: TicketPerson;
  read_status?: {
    is_read: boolean;
    read_at: string | null;
  };
}

export interface TicketAttachment {
  id: string;
  ticket_id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  uploaded_at: string;
  uploaded_by: string | null;
}

export interface TicketHistory {
  id: string;
  ticket_id: string;
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  changed_at: string;
  changed_by: string | null;
}

export interface CreateTicketInput {
  title: string;
  description: string;
  priority: TicketPriority;
  category: TicketCategory;
  loom_link?: string;
  project_id?: string;
  due_date?: string;
  contact_id?: string;
}

export interface UpdateTicketInput {
  title?: string;
  description?: string;
  status?: TicketStatus;
  priority?: TicketPriority;
  internal_priority?: TicketPriority;
  category?: TicketCategory;
  assigned_to?: string;
  project_id?: string;
  due_date?: string;
  loom_link?: string;
}

export interface CreateCommentInput {
  ticket_id: string;
  content: string;
  is_internal?: boolean;
  mentioned_user_ids?: string[];
}

export interface CreateAttachmentInput {
  ticket_id: string;
  file: File;
} 